import * as _ from 'lodash';
import * as moment from 'moment';
import { getAppInjector } from '../../../services/app-injector';
import { LocalStorageService } from '../../../services/local-storage.service';


enum CacheType {
  Views = 'views',
  ViewDashboards = 'view-dashboards',
  View = 'view',
  Survey = 'survey',
  ClientDataSet = 'client-data-set',
  Dashboard = 'dashboard',
  SelfDashboardFilters = 'self-dashboard-filters',
  SelfDashboardsFilters = 'self-dashboards-filters',
  SelfSurveyFilters = 'self-survey-filters',
  GridsterDashboard = 'gridster-dashboard',
  GridsterSurveyResults = 'gridster-survey-results',
  InsightDashboard = 'insight-dashboard',
  Insights = 'insights',
  Insight = 'insight',
  SurveyFilter = 'survey-filter',
  SurveyFilters = 'survey-filters',
  GridsterEmployeeCampaignsResults = 'gridster-employee-campaigns-results',
  SurveyResponses = 'SurveyResponses',
  SurveyDetails = 'SurveyDetails',
  LastUsedValues = 'LastUsedValues',
}

export class AppCache {
  private static instance: AppCache;
  localCache: any;
  defaultCacheExpiryInSeconds = 60 * 3; // 3 minutes

  // Resolved lazily (not in the constructor) via the app-injector bridge, since AppCache is a
  // manual singleton predating Angular DI and may be constructed before Angular finishes
  // bootstrapping. Routes ALL storage access through LocalStorageService so quota-exceeded
  // handling (toast + non-throwing writes) lives in exactly one place.
  private get localStorageService(): LocalStorageService {
    return getAppInjector().get(LocalStorageService);
  }

  private constructor() {
    this.localCache = {};
  }

  public static getInstance(): AppCache {
    if (!AppCache.instance) {
      AppCache.instance = new AppCache();
    }
    return AppCache.instance;
  }

  public static cacheTypes() {
    return CacheType;
  }

  /**
   * get's from in-memory cache
   * @param cacheType
   * @param key
   */
  public getCache(cacheType: CacheType, key: string) {
    const v = this.localCache[`cache ${cacheType.toString()}-${key}`];
    if (_.isNil(v) || _.isNil(v.expireInSeconds)) {
      return v;
    } else {
      if (typeof (v.cachedAt) === 'string') {
        v.cachedAt = new Date(v.cachedAt);
      }
      if (((new Date().getTime() - v.cachedAt.getTime()) / 1000) < v.expireInSeconds) {
        return v.value;
      } else {
        delete this.localCache[`cache ${cacheType.toString()}-${key}`];
        return undefined;
      }
    }
  }

  public resetCache() {
    this.localCache = {};
    this.localStorageService.clearAllRaw();
  }

  public getScreenshotURL() {
    return this.localCache.screenshorURL;
  }

  public setScreenshotURL(url) {
    this.localCache.screenshorURL = url;
  }

  public addMessage(message: string) {
    this.localCache.appMessage += (new Date()) + ' ' + message;
  }

  public getMessages() {
    return this.localCache.appMessage;
  }

  /**
   * Sets to in-memory cache
   * @param cacheType
   * @param key
   * @param value
   */
  public setCache(cacheType: CacheType, key: string, value: any) {
    // this.localCache[`cache ${cacheType.toString()}-${key}`] = value;
    this.setCacheWithExpiry(cacheType, key, value, this.defaultCacheExpiryInSeconds);
  }

  public setCacheWithExpiry(cacheType: CacheType, key: string, value: any, expireInSeconds = 300) {
    this.localCache[`cache ${cacheType.toString()}-${key}`] = {expireInSeconds, value, cachedAt: new Date()};
  }

  /**
   * get's from local storage
   * @param cacheType
   * @param key
   */
  public getLocalCache(cacheType: CacheType, key: string): string {
    const dateCached = this.localStorageService.getRaw(`date-cached ${cacheType.toString()}-${key}`);
    if (_.isNil(dateCached) || dateCached && moment().diff(moment(dateCached), 'seconds') > this.defaultCacheExpiryInSeconds) {
      // console.log('clearing cache dateCached >>> ', dateCached, typeof dateCached)
      this.clearLocalCache(cacheType, key);
      return undefined;
    }
    const item = this.localStorageService.getRaw(`cache ${cacheType.toString()}-${key}`);
    return item === 'undefined' ? undefined : item;
  }

  // public clearAllCache(cacheType: CacheType, key: string): void {
  //   this.localStorage.clear();
  // }

  public clearLocalCache(cacheType: CacheType, key: string): void {
    this.localStorageService.removeRaw(`date-cached ${cacheType.toString()}-${key}`);
    this.localStorageService.removeRaw(`cache ${cacheType.toString()}-${key}`);
  }

  /**
   * set's from local storage. Best-effort: if the write fails (e.g. local storage is full),
   * LocalStorageService logs it and shows the user an actionable toast — it does NOT throw, so
   * callers relying on this for a "nice to have" cache (like the dashboard save flow) are never
   * blocked by a storage failure that has nothing to do with the actual save.
   * @param cacheType
   * @param key
   * @param value
   */
  public setLocalCache(cacheType: CacheType, key: string, value: string) {
    this.localStorageService.setRaw(`date-cached ${cacheType.toString()}-${key}`, moment().toISOString());
    this.localStorageService.setRaw(`cache ${cacheType.toString()}-${key}`, value);
  }

}
